import {
  PROFILER_SEARCH_FIELDS as CHARACTERISTICS,
  PROFILER_INDUSTRY_CODES_SEARCH_FIELDS as INDUSTRIES
} from '../utils/constants/fields';
import { UI_OPTIONS } from '../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../utils/constants/uiOptionKeys';
import {
  collapseIndustryCodes,
  industrySectionsFromCodes
} from '../utils/profiler/collapseIndustryCodes';
import { resolveOwnershipBasis } from '../utils/ownershipCriteria';

// GA4 truncates an event-parameter value at 100 characters.
const GA4_PARAM_MAX_LENGTH = 100;

const truncateToGa4Limit = (value = '') =>
  value.length > GA4_PARAM_MAX_LENGTH ? value.slice(0, GA4_PARAM_MAX_LENGTH) : value;

/**
 * Builds the non-PII parameter object for the `search_executed` event.
 *
 * GDPR: free-text keyword fields (industry / value chain / product portfolio) are
 * reduced to a COUNT, never the text. City/zipcode is reflected only as an active-filter
 * flag. Numeric range values and selected industry codes are non-PII and are included.
 *
 * GA4 efficiency: which filters are active is reported as a single `filters_used` string
 * (one custom dimension) instead of many `has_*` booleans; the range values stay numeric
 * so GA4 can register them as custom metrics rather than dimensions.
 */

const keywordCount = (val) =>
  typeof val === 'string'
    ? val
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean).length
    : 0;

const toNum = (v) => {
  const raw = Array.isArray(v) ? v[0] : v;
  if (raw === '' || raw === undefined || raw === null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const hasValue = (v) =>
  Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && String(v).trim() !== '';

const count = (v) => (Array.isArray(v) ? v.length : hasValue(v) ? 1 : 0);

// Ownership criteria: counts only, plus the counted basis (a fixed enum, non-PII) when
// a shareholder structure is set; the basis means nothing without one.
const ownershipFilters = (fields, set) => {
  const listing = fields[set.LISTING_STATUS.NAME];
  const structures = fields[set.OWNERSHIP_STRUCTURES.NAME];
  const active = [];
  if (hasValue(listing)) active.push('listing_status');
  if (hasValue(structures)) active.push('shareholder_structure');
  return {
    active,
    values: {
      listing_status_count: count(listing),
      shareholder_structure_count: count(structures),
      ownership_basis: hasValue(structures)
        ? resolveOwnershipBasis(fields[set.OWNERSHIP_BASIS.NAME])
        : ''
    }
  };
};

/**
 * Maps a raw profiler search-category value (e.g. 'companyCriteria', 'industryCodes') to its
 * readable name ('Characteristics', 'Industries', ...), so `search_executed` and
 * `search_results_displayed` share ONE vocabulary for the search_type dimension.
 * @param {string} categoryValue
 * @returns {string}
 */
export const readableProfilerSearchType = (categoryValue) =>
  Object.entries(UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]).find(
    ([, value]) => value === categoryValue
  )?.[0] ||
  categoryValue ||
  '';

// Numeric range + geo filter values (→ GA4 metrics) plus the list of which groups are active.
const sharedFilters = (fields, F) => {
  const active = [];
  if (hasValue(fields[F.REVENUE_MIN.NAME]) || hasValue(fields[F.REVENUE_MAX.NAME]))
    active.push('sales');
  if (hasValue(fields[F.CAGR_MIN.NAME]) || hasValue(fields[F.CAGR_MAX.NAME])) active.push('cagr');
  if (hasValue(fields[F.EBIT_MARGIN_MIN.NAME]) || hasValue(fields[F.EBIT_MARGIN_MAX.NAME]))
    active.push('ebit_margin');
  if (hasValue(fields[F.EMPLOYEES_MIN.NAME]) || hasValue(fields[F.EMPLOYEES_MAX.NAME]))
    active.push('employees');
  if (hasValue(fields[F.HEADQUARTER.NAME])) active.push('country');
  if (hasValue(fields[F.CITY_ZIPCODE.NAME])) active.push('location');
  return {
    active,
    values: {
      sales_min: toNum(fields[F.REVENUE_MIN.NAME]),
      sales_max: toNum(fields[F.REVENUE_MAX.NAME]),
      cagr_min: toNum(fields[F.CAGR_MIN.NAME]),
      cagr_max: toNum(fields[F.CAGR_MAX.NAME]),
      ebit_margin_min: toNum(fields[F.EBIT_MARGIN_MIN.NAME]),
      ebit_margin_max: toNum(fields[F.EBIT_MARGIN_MAX.NAME]),
      employees_min: toNum(fields[F.EMPLOYEES_MIN.NAME]),
      employees_max: toNum(fields[F.EMPLOYEES_MAX.NAME]),
      country_count: count(fields[F.HEADQUARTER.NAME])
    }
  };
};

export const buildCharacteristicsSearchParams = (fields = {}) => {
  const { active, values } = sharedFilters(fields, CHARACTERISTICS);
  const industryKeywords = keywordCount(fields[CHARACTERISTICS.INDUSTRY.NAME]);
  const valueChainKeywords = keywordCount(fields[CHARACTERISTICS.VALUE_CHAIN.NAME]);
  const productPortfolioKeywords = keywordCount(fields[CHARACTERISTICS.PRODUCT_PORTFOLIO.NAME]);
  const filters = [...active];
  if (industryKeywords > 0) filters.push('industry_keywords');
  if (valueChainKeywords > 0) filters.push('value_chain_keywords');
  if (productPortfolioKeywords > 0) filters.push('product_portfolio_keywords');
  if (hasValue(fields[CHARACTERISTICS.SITUATION.NAME])) filters.push('situation');
  const ownership = ownershipFilters(fields, CHARACTERISTICS);
  filters.push(...ownership.active);
  return {
    search_profile: 'corporates',
    search_type: 'Characteristics',
    industry_keyword_count: industryKeywords,
    value_chain_keyword_count: valueChainKeywords,
    product_portfolio_keyword_count: productPortfolioKeywords,
    situation_count: count(fields[CHARACTERISTICS.SITUATION.NAME]),
    ...ownership.values,
    ...values,
    // Single dimension listing which filter groups were active (replaces ~10 has_* booleans).
    filters_used: filters.join(','),
    total_filters_applied: filters.length
  };
};

export const buildIndustriesSearchParams = (fields = {}) => {
  // The field already holds the covering set (the picker autoruns collapse it), but
  // collapse defensively here too: this builder is the last stop before GA4, and a
  // fully expanded list would silently truncate to ~13 codes against the cap below.
  const codes = collapseIndustryCodes(fields[INDUSTRIES.INDUSTRY_CODES.NAME]);
  const { active, values } = sharedFilters(fields, INDUSTRIES);
  const industriesCount = codes.length;
  const filters = [...active];
  if (industriesCount > 0) filters.push('industries');
  if (hasValue(fields[INDUSTRIES.SITUATION.NAME])) filters.push('situation');
  const ownership = ownershipFilters(fields, INDUSTRIES);
  filters.push(...ownership.active);
  return {
    search_profile: 'corporates',
    search_type: 'Industries',
    // Codes the user actually picked, not the cascaded subtree.
    industries_select_count: industriesCount,
    // Distinct top-level sections, e.g. 'C,G'. There are only 21 of them, so this
    // is the one industry dimension that can never hit the cap — segment on it.
    industry_sections: industrySectionsFromCodes(codes).join(','),
    // Selected industry codes — a public taxonomy (non-PII), joined like the
    // existing fund-creation events. Capped so a very wide pick degrades visibly
    // rather than being silently cut off by GA4.
    industry_codes: truncateToGa4Limit(codes.join(' - ')),
    situation_count: count(fields[INDUSTRIES.SITUATION.NAME]),
    ...ownership.values,
    ...values,
    filters_used: filters.join(','),
    total_filters_applied: filters.length
  };
};
